/**
 * Turning a parsed `/usage` report into the thing a host row stores, and the
 * freshness rules every consumer of it must obey (ENG-9394).
 *
 * This is deliberately pure and lives in core rather than in the CLI poller,
 * because the rules it encodes are the whole fix and they have to be assertable:
 *
 *   1. A reading EXPIRES. `read_at` is the instant the value was observed, and a
 *      consumer refuses a reading older than ~2 poll intervals. The precise bug
 *      being replaced is a fresh-looking timestamp attached to a frozen value -
 *      on 2026-08-24 thirteen `usage_limit_reached` criticals carried readings
 *      between one and five hours old, presented as current, and every one was
 *      false.
 *   2. UNKNOWN NEVER COLLAPSES TO AT-LIMIT. A missing, expired or unparseable
 *      reading gates nothing. Failing closed here starves the fleet, which is a
 *      worse outcome than the alert this replaces. Every path that cannot
 *      produce a number produces `unknown`, and `unknown` is not `100`.
 *   3. A host that is not on a subscription is `not_applicable` - not 0%, not
 *      unknown. Zero would read as "plenty of headroom" and unknown would read
 *      as "the poller is broken"; neither is true of an API-key host that has no
 *      subscription quota at all.
 */
import type { ResetPrecision } from './reset-time.js';
import type { UsageReport } from './usage-report-parser.js';

/**
 * What a host row can say about Claude usage.
 *
 * `ok` - a fresh reading exists and the percentages mean something.
 * `unknown` - we do not know. Never gate on this, and never render it as 0 or 100.
 * `not_applicable` - this host has no subscription quota to report.
 */
export type HostUsageState = 'ok' | 'unknown' | 'not_applicable';

/**
 * Why a reading is `unknown`. Carried so an operator can tell a broken poller
 * from a host nobody has polled yet, without reading logs. These are POLLER
 * faults, and an alert raised from one must be about the poller, never about the
 * host's usage.
 *
 * `clock_skew` vs `expired` is the one distinction here that costs an operator
 * real time when it is wrong (Ada's review of #4988). Both mean "we will not
 * trust this number", but they are DIFFERENT INCIDENTS with different owners:
 *
 *   expired    - nothing has reported for ~2 poll intervals. The poller is dead,
 *                the host is offline, or the CLI is wedged. Go look at the host.
 *   clock_skew - the host is polling perfectly, every 15 minutes, and stamping
 *                its readings with a time that has not happened yet. Nothing is
 *                wrong with the poller; the box's clock is wrong. Go look at NTP.
 *
 * Before this reason existed a forward-skewed host reported as `expired`, which
 * reads as "this host stopped polling" - the exact opposite of what is true, and
 * indistinguishable in the data from a genuinely dead poller.
 */
export type HostUsageUnknownReason =
  | 'never_polled'
  | 'poll_failed'
  | 'unparseable_output'
  | 'expired'
  | 'clock_skew';

/** How the reading was taken. Mirrors the vocabulary on `claude_code_usage_observations.source`. */
export const HOST_USAGE_SOURCE = 'cli_print_usage' as const;

export interface HostUsageReading {
  state: HostUsageState;
  /** 0-100, or null unless `state === 'ok'`. */
  weekPct: number | null;
  weekResetsAt: Date | null;
  /** 0-100, or null. Session and week are SEPARATE (ENG-9007 / ENG-9121). */
  sessionPct: number | null;
  sessionResetsAt: Date | null;
  /** The instant the value was actually observed. Never a write time. */
  readAt: Date;
  /**
   * The Claude Code version that produced the text. Recorded because print-mode
   * `/usage` is a CLI-version fact, not a contract: it did not work in May 2026
   * (PR #1307) and does on 2.1.241. A downgrade or a reword must be diagnosable
   * as such rather than looking like a fleet-wide usage event.
   */
  cliVersion: string | null;
  source: typeof HOST_USAGE_SOURCE;
  unknownReason: HostUsageUnknownReason | null;
}

export interface BuildHostUsageReadingInput {
  /** Parsed report, or null when the CLI answered in a shape we do not know. */
  report: UsageReport | null;
  readAt: Date;
  cliVersion: string | null;
  /**
   * The host's configured auth mode. Anything other than `subscription` yields
   * `not_applicable` - an API-key, OpenRouter or xAI host has no subscription
   * quota, so reporting 0% would be a false all-clear.
   */
  authMode: string | null | undefined;
  /** Set when the poll itself failed (non-zero exit, timeout, spawn error). */
  pollFailed?: boolean;
}

function unknown(
  reason: HostUsageUnknownReason,
  readAt: Date,
  cliVersion: string | null,
): HostUsageReading {
  return {
    state: 'unknown',
    weekPct: null,
    weekResetsAt: null,
    sessionPct: null,
    sessionResetsAt: null,
    readAt,
    cliVersion,
    source: HOST_USAGE_SOURCE,
    unknownReason: reason,
  };
}

/**
 * Build the reading a host row should store from one poll.
 *
 * Order matters: auth mode is checked BEFORE the report, so a non-subscription
 * host is `not_applicable` even when the CLI happens to print something.
 */
export function buildHostUsageReading(input: BuildHostUsageReadingInput): HostUsageReading {
  const { report, readAt, cliVersion, authMode, pollFailed } = input;

  if (authMode != null && authMode !== 'subscription') {
    return {
      state: 'not_applicable',
      weekPct: null,
      weekResetsAt: null,
      sessionPct: null,
      sessionResetsAt: null,
      readAt,
      cliVersion,
      source: HOST_USAGE_SOURCE,
      unknownReason: null,
    };
  }

  if (pollFailed) return unknown('poll_failed', readAt, cliVersion);
  if (!report) return unknown('unparseable_output', readAt, cliVersion);

  // The CLI rendered a report but said this login is not a subscription. Trust
  // the CLI over the stored column: the column is configuration and this is an
  // observation of what the runtime is actually using.
  if (!report.subscription) {
    return {
      state: 'not_applicable',
      weekPct: null,
      weekResetsAt: null,
      sessionPct: null,
      sessionResetsAt: null,
      readAt,
      cliVersion,
      source: HOST_USAGE_SOURCE,
      unknownReason: null,
    };
  }

  // A report with neither window is not a reading. This is reachable if the CLI
  // keeps the subscription line and reworks the window lines.
  if (!report.week && !report.session) {
    return unknown('unparseable_output', readAt, cliVersion);
  }

  return {
    state: 'ok',
    weekPct: report.week?.pct ?? null,
    // ONLY a DATED reset is stored (CodeRabbit on #4988).
    //
    // Print mode has always rendered a date, so `hour_only` here means the CLI
    // changed its render and `parseResetDateTime` reconstructed the day from an
    // hour. That reconstruction can never be more than 24h out, and storing it
    // in a column consumers treat as a measurement is precisely ENG-8472: every
    // host whose render named the same hour landed on the same millisecond, and
    // eleven days went into reading that as evidence of a shared subscription.
    //
    // The PERCENTAGE survives - it is a real reading either way, and dropping it
    // would blind the fleet over a formatting change. Only the boundary is
    // withheld, which degrades an alert to "cannot auto-close at reset" rather
    // than to "asserts a reset instant nobody measured".
    weekResetsAt: datedResetOnly(report.week),
    sessionPct: report.session?.pct ?? null,
    sessionResetsAt: datedResetOnly(report.session),
    readAt,
    cliVersion,
    source: HOST_USAGE_SOURCE,
    unknownReason: null,
  };
}

/**
 * A reset instant is only worth storing when it was READ. See the note at the
 * call site - a reconstructed instant presented as a measurement is the ENG-8472
 * defect, and this is the one line that stops it reaching the host row.
 */
function datedResetOnly(
  window: { resetsAt: Date | null; resetPrecision: ResetPrecision | null } | null | undefined,
): Date | null {
  if (!window || !window.resetsAt) return null;
  return window.resetPrecision === 'dated' ? window.resetsAt : null;
}

/**
 * Host-level poll cadence.
 *
 * It lives HERE rather than beside the poller because it is not a producer
 * detail: every freshness and skew rule below is expressed in multiples of it,
 * and the server applies those rules to readings the CLI produced. Two copies of
 * this number in two packages is two things to keep in step, and the failure
 * when they drift is silent - a server that thinks the cadence is faster than it
 * is expires every reading, a server that thinks it is slower trusts stale ones.
 * `apps/cli/src/lib/host-usage-poller.ts` re-exports this one.
 */
export const HOST_USAGE_POLL_INTERVAL_MS = 15 * 60 * 1000;

/**
 * How many poll intervals a reading stays usable. Two, so a single missed poll
 * does not blind the fleet but a dead poller does surface within ~30 minutes at
 * the 15-minute cadence.
 */
export const READING_EXPIRY_INTERVALS = 2;

/**
 * How far AHEAD of the server a reading may be stamped before we call it a clock
 * problem rather than a reading. One poll interval.
 *
 * Not zero, deliberately. Two machines are never exactly in step and a reading
 * stamped a few seconds into the server's future is ordinary NTP slop, not an
 * incident - raising `clock_skew` on that would replace one misdiagnosis with a
 * noisier one. One interval is wide enough that no amount of normal drift or
 * request latency reaches it, and narrow enough that a host far enough ahead to
 * matter (the pathological case is a box that never synced and is hours or days
 * out) is caught on its very first post.
 */
export const CLOCK_SKEW_AHEAD_INTERVALS = 1;

/**
 * Is this reading stamped so far in the server's future that the host's clock,
 * not the host's poller, is the thing that is broken?
 *
 * Strictly greater than: a reading exactly one interval ahead is at the edge of
 * tolerated drift, not past it.
 */
export function isClockSkewedAhead(
  readAt: Date,
  now: Date,
  pollIntervalMs: number,
  intervals: number = CLOCK_SKEW_AHEAD_INTERVALS,
): boolean {
  return readAt.getTime() - now.getTime() > pollIntervalMs * intervals;
}

/**
 * Is this reading still current?
 *
 * The one line that would have prevented the 2026-08-24 incident on its own: any
 * consumer that had refused a reading older than ~2 poll intervals would have
 * discarded all thirteen false criticals with no other change to the system.
 */
export function isReadingFresh(
  readAt: Date,
  now: Date,
  pollIntervalMs: number,
  intervals: number = READING_EXPIRY_INTERVALS,
): boolean {
  const age = now.getTime() - readAt.getTime();
  // A reading from the future is a clock problem, not a fresh reading. Treat it
  // as stale rather than trusting it indefinitely.
  if (age < 0) return false;
  return age <= pollIntervalMs * intervals;
}

/**
 * Apply expiry to a stored reading. An expired `ok` becomes `unknown` - it never
 * becomes at-limit, and it never silently stays `ok`.
 *
 * `not_applicable` does not expire: a host without a subscription does not
 * acquire one by going unpolled.
 *
 * THE VERDICT AND THE REASON ARE DECIDED SEPARATELY, on purpose. `isReadingFresh`
 * already refuses a future-dated reading and that safety property is untouched
 * here - a skewed host still gates nothing. What changes is only what the row
 * SAYS about why, because `expired` on a host that is polling fine every fifteen
 * minutes sends an operator to look for a dead poller that does not exist. A
 * reading dated after `now` did not go stale; the clock that stamped it is wrong.
 */
export function expireReading(
  reading: HostUsageReading,
  now: Date,
  pollIntervalMs: number,
): HostUsageReading {
  if (reading.state !== 'ok') return reading;
  if (isReadingFresh(reading.readAt, now, pollIntervalMs)) return reading;
  const reason: HostUsageUnknownReason =
    reading.readAt.getTime() > now.getTime() ? 'clock_skew' : 'expired';
  return unknown(reason, reading.readAt, reading.cliVersion);
}

/**
 * How far two resolved reset instants may differ and still be treated as the
 * SAME window. Five minutes: comfortably wider than the observed jitter, and
 * orders of magnitude narrower than the gap between two genuinely different
 * windows, which are days apart.
 */
export const RESET_JITTER_TOLERANCE_MS = 5 * 60 * 1000;

/**
 * Hold a reset instant steady across polls of the same window.
 *
 * MEASURED, not defensive. The CLI's reset string is ROUNDED, and the rounding
 * is not stable across reads of the SAME unchanged window. On a live host,
 * minute-apart polls rendered:
 *
 *   00:41Z, 00:42Z, 00:43Z  ->  "resets Aug 25, 12:59am (UTC)"
 *   00:44Z, 00:45Z, 00:46Z  ->  "resets Aug 25, 1am (UTC)"
 *
 * Those resolve 60 seconds apart. The existing usage-limit dedupe key embeds the
 * exact ISO instant for a `dated` reading, so keying a poller on the raw value
 * would mint a fresh key - and therefore a fresh alert - each time the render
 * flipped, while the operator saw one unchanging window. That is the
 * alert-flapping cousin of the bug this ticket exists to fix.
 *
 * WHY NOT JUST ROUND TO A GRID. That was the first attempt and it is wrong: any
 * fixed grid has boundaries, and this jitter straddles one. Flooring 00:59 and
 * 01:00 to five-minute buckets yields 00:55 and 01:00 - two buckets for one
 * window, exactly the flap the rounding was meant to remove. Rounding to the
 * nearest bucket happens to survive THIS pair and fails for a window sitting
 * near a different boundary. A grid cannot fix jitter; it can only move where
 * the jitter hurts.
 *
 * So compare against what we already stored. If the new instant is within
 * tolerance of the previous one it IS the previous one, and the stored value
 * never moves. When the window genuinely rolls over, the new instant is days
 * away, far outside tolerance, and it replaces the old one cleanly.
 */
export function stabiliseResetInstant(
  previous: Date | null | undefined,
  next: Date | null,
  toleranceMs: number = RESET_JITTER_TOLERANCE_MS,
): Date | null {
  if (!next) return null;
  if (!previous) return next;
  return Math.abs(next.getTime() - previous.getTime()) <= toleranceMs ? previous : next;
}
